import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Activity,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Lightbulb,
  Link2,
  Users,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  type ActivitySignal,
  type ApprovalListItem,
  type ApprovalOriginChannel,
  type KeyStrength,
  type PreviewResolution,
  useApprovalOrigin,
  usePreviewResolution,
} from "../lib/approvals";
import { useAddDenylistEntry } from "../lib/denylist";
import { PresendNotice, RULE_CHECK_FAILED } from "./PresendNotice";
import { ApprovalTouchpointInsert } from "./ApprovalTouchpointInsert";
import { MergeGraph } from "./MergeGraph";

interface Props {
  item: ApprovalListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (id: number, droppedKeys?: string[]) => void;
  onReject: (id: number) => void;
}

export function EventDetailDrawer({
  item,
  open,
  onOpenChange,
  onApprove,
  onReject,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [droppedKeys, setDroppedKeys] = useState<Set<string>>(new Set());
  // Technical analysis stays collapsed by default — the plain-language summary
  // above it is all most reviewers need to decide.
  const [showTech, setShowTech] = useState(false);
  // Dialog.Content's own DOM node (TASK-80) — state, not a plain ref, so
  // ApprovalTouchpointInsert re-renders with the real node once Radix
  // mounts it instead of staying stuck on the initial `null`. Forwarded down
  // to MetaCampaignFields' Combobox instances as their portal container: a
  // modal Radix dialog disables pointer events everywhere outside this
  // exact node, so the option lists must portal inside it, not
  // `document.body`.
  const [dialogContentEl, setDialogContentEl] = useState<HTMLDivElement | null>(
    null,
  );

  // Reset selection when switching events.
  useEffect(() => {
    setDroppedKeys(new Set());
    setShowTech(false);
  }, [item?.id]);

  const isMergeReview = item?.gateType === "merge_review";
  const previewEnabled = Boolean(isMergeReview && open && item);
  const previewQuery = usePreviewResolution(
    item?.id ?? 0,
    [...droppedKeys].sort(),
    previewEnabled,
  );
  const preview = previewEnabled ? previewQuery.data : undefined;
  const previewError = previewEnabled
    ? (previewQuery.error as Error | null)
    : null;

  const json = item ? JSON.stringify(item.payload, null, 2) : "";

  const copy = async () => {
    if (!json) return;
    await navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const toggleKey = (keyType: string) => {
    setDroppedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(keyType)) next.delete(keyType);
      else next.add(keyType);
      return next;
    });
  };

  const eventKeys =
    (item?.payload["keys"] as Record<string, string> | undefined) ?? {};
  const keyEntries = Object.entries(eventKeys).filter(
    ([, v]) => v && v.trim() !== "",
  );

  const droppedArray = [...droppedKeys];

  // Denylist: scoped to the item's project so the "add to denylist" buttons
  // know which project to write to and which query cache to invalidate.
  const qc = useQueryClient();
  const addDenylist = useAddDenylistEntry(item?.projectId ?? 0);
  const banFromMerging = (keyType: string, value: string) => {
    if (!item) return;
    addDenylist.mutate(
      {
        keyType,
        value,
        reason: `Marked from merge-review of event ${item.id}`,
      },
      {
        onSuccess: () => {
          toast.success(`${keyType}=${value.slice(0, 30)}… auf Denylist`);
          // Force preview refresh so the bridges disappear immediately.
          qc.invalidateQueries({
            queryKey: ["admin", "approvals", "preview", item.id],
          });
        },
        onError: (err) =>
          toast.error(`Denylist-Eintrag fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-overlay bg-overlay animate-fade-in" />
        <Dialog.Content
          ref={setDialogContentEl}
          className="fixed right-0 top-0 z-modal flex h-full w-drawer max-w-full flex-col border-l border-border-primary bg-bg-primary shadow-overlay animate-slide-in-right"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between border-b border-border-primary px-5 py-3">
            <Dialog.Title className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Event #{item?.id}
              {isMergeReview && (
                <span className="rounded-full bg-warning-bg px-2 py-0.5 text-[10px] font-semibold text-warning">
                  Merge-Review
                </span>
              )}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
              >
                <X size={14} />
              </button>
            </Dialog.Close>
          </div>

          {item && (
            <div className="flex-1 overflow-auto p-5">
              <div className="space-y-1.5 text-sm">
                <Field label="Projekt" value={item.projectName} />
                <Field label="Event-Typ" value={item.eventType} />
                {item.firstName && (
                  <Field label="Vorname" value={item.firstName} />
                )}
                {item.lastName && (
                  <Field label="Nachname" value={item.lastName} />
                )}
                {item.email && <Field label="Email" value={item.email} mono />}
                {item.dealValue !== null && (
                  <Field
                    label="Deal Value"
                    value={`${item.dealValue.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.currency ?? ""}`}
                    mono
                  />
                )}
                {/* Full, untruncated name — the list column has to cut it off
                    (TASK-91.2). Same field, no second parse. */}
                {item.productName && (
                  <Field label="Produkt" value={item.productName} />
                )}
                <Field
                  label="Erstellt"
                  value={new Date(item.createdAt).toLocaleString("de-DE")}
                  mono
                />
              </div>

              {/* Über dem Payload, für jede Freigabe sichtbar (auch
                  Merge-Review) — die Herkunft ist eine Hilfe vor dem
                  Entscheiden, kein Sperrgrund und keine "Technische
                  Details"-Randnotiz. */}
              <PresendNotice at={item.capiPresendAt} />
              {item.ruleCheckFailed && (
                <div role="note" className="rounded-md border border-danger bg-danger-bg px-4 py-3 text-sm text-danger">
                  <p className="font-semibold">{RULE_CHECK_FAILED}</p>
                  <p className="mt-1 text-xs">
                    Keine Regel hat diesen Fall hierher geschickt: die Prüfung selbst ist technisch
                    gescheitert. Er wurde nicht an Meta/Google gemeldet.
                  </p>
                </div>
              )}

              <OriginBlock approvalId={item.id} open={open} />

              {/* TASK-76 (A3/A4/A6): "Touchpoint einfügen" directly under
                  the origin block — every gate EXCEPT merge_review (that
                  gate is about identity, not origin). Fully self-contained;
                  never gates Genehmigen/Ablehnen below. */}
              {!isMergeReview && (
                <ApprovalTouchpointInsert
                  item={item}
                  open={open}
                  dialogContainer={dialogContentEl}
                />
              )}

              {isMergeReview && (
                <div className="mt-6 space-y-4">
                  <MergeReviewSummary item={item} preview={preview} />

                  <button
                    type="button"
                    onClick={() => setShowTech((v) => !v)}
                    className="flex w-full items-center gap-1.5 rounded-md border border-border-secondary bg-bg-secondary px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-hover"
                  >
                    {showTech ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    Technische Details {showTech ? "ausblenden" : "anzeigen"}
                    <span className="ml-1 text-text-tertiary">
                      (Keys, Live-Vorschau, Brücken)
                    </span>
                  </button>

                  {showTech && (
                  <div className="space-y-4">
                  {/* Stale-gate detection: the merge_risk reasons stored on
                      the pending row are a snapshot from the moment the
                      worker gated the event (possibly days ago, possibly
                      against a different identity engine). The live preview
                      runs against the current state. When they disagree —
                      reasons exist but live says "new visitor, no merge" —
                      the underlying condition has already resolved itself
                      and approving is safe. */}
                  {item.mergeRisk &&
                    item.mergeRisk.reasons.length > 0 &&
                    preview?.resolution.newVisitor === true && (
                      <div className="rounded-md border border-success bg-success-bg px-3 py-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-success">
                          <Check size={14} />
                          Re-Evaluation: kein Merge mehr nötig
                        </div>
                        <p className="mt-1 text-xs text-text-secondary">
                          Der Gate-Auslöser ist mit dem aktuellen Datenstand
                          nicht mehr gegeben — würde jetzt einfach als neuer
                          Visitor angelegt. Approve as-is ist sicher.
                        </p>
                      </div>
                    )}

                  {item.mergeRisk &&
                    item.mergeRisk.reasons.length > 0 &&
                    preview?.resolution.newVisitor !== true && (
                      <div className="rounded-md border border-warning bg-warning-bg px-3 py-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-warning">
                          <AlertTriangle size={14} />
                          Risiko-Gründe
                        </div>
                        <ul className="mt-1 ml-6 list-disc text-xs text-text-secondary">
                          {item.mergeRisk.reasons.map((r) => (
                            <li key={r}>{translateReason(r)}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Auto-Empfehlung: erscheint nur wenn ausschließlich
                      schwache Bridges existieren — ein-Klick-Anwendung
                      setzt droppedKeys auf die Vorschlagsliste. */}
                  {preview?.bridgeSummary.suggestedDropKeys &&
                    preview.bridgeSummary.suggestedDropKeys.length > 0 && (
                      <div className="rounded-md border border-accent bg-accent-subtle px-3 py-2.5">
                        <div className="flex items-start gap-2">
                          <Lightbulb
                            size={14}
                            className="mt-0.5 flex-shrink-0 text-accent"
                          />
                          <div className="flex-1 space-y-1.5">
                            <p className="text-xs font-medium text-accent">
                              Empfehlung: Drop „
                              {preview.bridgeSummary.suggestedDropKeys.join(", ")}
                              " + Approve
                            </p>
                            {preview.bridgeSummary.suggestedReason && (
                              <p className="text-[11px] text-text-secondary">
                                {preview.bridgeSummary.suggestedReason}
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setDroppedKeys(
                                  new Set(
                                    preview.bridgeSummary.suggestedDropKeys ?? [],
                                  ),
                                );
                              }}
                              className="rounded-md bg-accent px-2 py-1 text-[11px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
                            >
                              Vorschlag übernehmen
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Graph-Visualisierung — nur sinnvoll ab 3 involved
                      visitors, wo die textuelle Bridges-Liste an Übersicht
                      verliert. */}
                  {preview && preview.involvedVisitors.length >= 3 && (
                    <MergeGraph preview={preview} />
                  )}

                  {/* Bridges — die eigentliche Antwort auf "welche Keys
                      verbinden welche Visitors". Nur sichtbar wenn ein
                      Merge tatsächlich passieren würde. */}
                  {preview && preview.mergeBridges.length > 0 && (
                    <div>
                      <p className="mb-2 flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                        <Link2 size={12} />
                        Merge-Brücken
                      </p>
                      <p className="mb-3 text-xs text-text-tertiary">
                        Diese Key-Werte verbinden den eingehenden Event mit
                        bestehenden Visitor-IDs. Farbe = Verlässlichkeit des
                        Keys.
                      </p>
                      <div className="space-y-1.5">
                        {preview.mergeBridges.map((b) => {
                          const dropped = droppedKeys.has(b.keyType);
                          return (
                            <div
                              key={`${b.keyType}-${b.value}`}
                              className={`flex items-center gap-2 rounded-md border px-2 py-2 transition-opacity ${
                                strengthBorderClass(b.strength)
                              } ${dropped ? "opacity-50" : ""}`}
                            >
                              <StrengthDot strength={b.strength} />
                              <span className="w-16 font-mono text-[11px] text-text-secondary">
                                {b.keyType}
                              </span>
                              <span
                                className={`flex-1 truncate font-mono text-[11px] text-text-primary ${
                                  dropped ? "line-through" : ""
                                }`}
                                title={b.originValue}
                              >
                                {b.value}
                              </span>
                              <span className="rounded-md bg-bg-tertiary px-1.5 py-0.5 font-mono text-[10px] text-text-tertiary">
                                → {b.visitorIds.join(", ")}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleKey(b.keyType)}
                                className={`rounded-md px-2 py-1 text-[10px] font-medium transition-colors ${
                                  dropped
                                    ? "bg-bg-tertiary text-text-secondary hover:bg-bg-hover"
                                    : "border border-border-primary bg-bg-secondary text-text-secondary hover:bg-danger-bg hover:text-danger"
                                }`}
                              >
                                {dropped ? "Wieder rein" : "Drop"}
                              </button>
                              <button
                                type="button"
                                onClick={() => banFromMerging(b.keyType, b.value)}
                                disabled={addDenylist.isPending}
                                title="Auf Denylist setzen — dieser exakte Wert wird nie wieder Visitor verschmelzen"
                                className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-[10px] font-medium text-text-secondary hover:bg-danger-bg hover:text-danger disabled:opacity-50"
                              >
                                🚫 Bannen
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                        Keys im Event — Live Cardinality
                      </p>
                      <p className="mb-3 text-xs text-text-tertiary">
                        Häkchen entfernen → Key wird beim Approve nicht
                        mit-resolved.
                      </p>
                      <div className="space-y-1.5">
                        {keyEntries.map(([keyType, value]) => {
                          const card = preview?.keyCardinalities.find(
                            (c) => c.keyType === keyType,
                          );
                          const dropped = droppedKeys.has(keyType);
                          const strength: KeyStrength | undefined =
                            card?.strength;
                          return (
                            <label
                              key={keyType}
                              className={`flex items-center gap-2 rounded-md border px-2 py-2 text-sm transition-colors ${
                                dropped
                                  ? "border-border-secondary bg-bg-tertiary opacity-60"
                                  : "border-border-primary bg-bg-secondary"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={!dropped}
                                onChange={() => toggleKey(keyType)}
                                aria-label={`${keyType} ${dropped ? "wieder einbeziehen" : "ausschließen"}`}
                              />
                              {strength && <StrengthDot strength={strength} />}
                              <span className="w-16 font-mono text-[11px] text-text-secondary">
                                {keyType}
                              </span>
                              <span
                                className={`flex-1 truncate font-mono text-[11px] ${
                                  dropped ? "line-through" : "text-text-primary"
                                }`}
                              >
                                {value}
                              </span>
                              {card !== undefined && (
                                <span
                                  className={`rounded-md px-1.5 py-0.5 font-mono text-[10px] ${
                                    card.suspect
                                      ? "bg-warning-bg text-warning"
                                      : "bg-bg-tertiary text-text-tertiary"
                                  }`}
                                  title={`${card.visitorCount} Visitor(s) teilen diesen exakten Wert bereits`}
                                >
                                  {card.visitorCount}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                        Betroffene Visitors
                      </p>
                      <p className="mb-3 text-xs text-text-tertiary">
                        Match-Keys sind hervorgehoben — das sind die Brücken.
                      </p>
                      {previewQuery.isFetching && !preview ? (
                        <p className="text-xs text-text-tertiary">Lade…</p>
                      ) : preview && preview.involvedVisitors.length === 0 ? (
                        <div className="rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2 text-xs text-text-tertiary">
                          Kein Match — neuer Visitor würde angelegt.
                        </div>
                      ) : preview ? (
                        <div className="space-y-2">
                          {preview.involvedVisitors.map((v) => (
                            <div
                              key={v.visitorId}
                              className={`rounded-md border px-2 py-2 ${
                                v.isCanonical
                                  ? "border-accent bg-accent-subtle"
                                  : "border-warning bg-warning-bg"
                              }`}
                            >
                              <div className="mb-1.5 flex items-center justify-between gap-2">
                                <span className="font-mono text-[11px] text-text-primary">
                                  Visitor{" "}
                                  <span className="font-semibold">
                                    {v.visitorId}
                                  </span>
                                </span>
                                {v.isCanonical ? (
                                  <span className="rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-text-on-accent">
                                    bleibt (canonical)
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-warning px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-text-on-accent">
                                    wird aufgelöst →{" "}
                                    {preview.resolution.canonicalVisitorId}
                                  </span>
                                )}
                              </div>
                              {v.activity && (
                                <div className="mb-1.5 flex items-center gap-2 text-[10px] text-text-tertiary">
                                  <Activity size={10} />
                                  <span>
                                    {v.activity.eventCount} Event
                                    {v.activity.eventCount === 1 ? "" : "s"}
                                  </span>
                                  {v.activity.daysSinceLastSeen !== null && (
                                    <span>
                                      ·{" "}
                                      {formatDaysAgo(
                                        v.activity.daysSinceLastSeen,
                                      )}
                                    </span>
                                  )}
                                  <ActivitySignalBadge
                                    signal={v.activity.signal}
                                  />
                                </div>
                              )}
                              <div className="space-y-1">
                                {v.keys.length === 0 ? (
                                  <p className="text-[11px] text-text-tertiary">
                                    Keine Keys.
                                  </p>
                                ) : (
                                  v.keys.map((k, idx) => (
                                    <div
                                      key={`${k.keyType}-${k.value}-${idx}`}
                                      className={`flex items-center gap-2 rounded px-1 py-0.5 ${
                                        k.matchesIncoming
                                          ? "bg-warning-bg/60"
                                          : ""
                                      }`}
                                    >
                                      <StrengthDot strength={k.strength} />
                                      <span className="w-14 font-mono text-[11px] text-text-secondary">
                                        {k.keyType}
                                      </span>
                                      <span
                                        className={`flex-1 truncate font-mono text-[11px] ${
                                          k.matchesIncoming
                                            ? "font-semibold text-warning"
                                            : "text-text-primary"
                                        }`}
                                        title={
                                          k.matchesIncoming
                                            ? "Match: dieser Key+Wert ist die Brücke zum Event"
                                            : k.value
                                        }
                                      >
                                        {k.value}
                                      </span>
                                      {k.matchesIncoming && (
                                        <span
                                          className="rounded-full bg-warning px-1 text-[8px] font-bold uppercase tracking-wider text-text-on-accent"
                                          title="Match mit eingehendem Event"
                                        >
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                      Was würde jetzt passieren
                    </p>
                    {previewQuery.isFetching && !preview && (
                      <div className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs text-text-tertiary">
                        Lade Live-Auflösung gegen BigQuery…
                      </div>
                    )}
                    {previewError && (
                      <div className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-xs text-danger">
                        Vorschau konnte nicht geladen werden: {previewError.message}
                      </div>
                    )}
                    {preview && (
                      <div className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs">
                        {preview.resolution.newVisitor ? (
                          <p className="text-text-primary">
                            Neuer Visitor wird angelegt — kein Merge.
                          </p>
                        ) : preview.resolution.wouldMergeCount > 0 ? (
                          <div className="space-y-1.5 text-warning">
                            <p>
                              <strong>
                                {preview.resolution.wouldMergeCount + 1}{" "}
                                bestehende Visitors werden zu einem
                                verschmolzen:
                              </strong>
                            </p>
                            <p className="text-text-primary">
                              Visitor{" "}
                              <span className="font-mono font-semibold">
                                {preview.resolution.canonicalVisitorId}
                              </span>{" "}
                              gewinnt (älteste ID, „canonical"). Visitor
                              {preview.resolution.mergeIds.length === 1
                                ? " "
                                : "s "}
                              <span className="font-mono">
                                {preview.resolution.mergeIds.join(", ")}
                              </span>{" "}
                              {preview.resolution.mergeIds.length === 1
                                ? "wird"
                                : "werden"}{" "}
                              auf{" "}
                              <span className="font-mono">
                                {preview.resolution.canonicalVisitorId}
                              </span>{" "}
                              umgeschrieben und {" "}
                              {preview.resolution.mergeIds.length === 1
                                ? "verschwindet"
                                : "verschwinden"}{" "}
                              als eigenständige Visitor-IDs. Alle Keys, Events
                              und Visitor-Historie wandern mit.
                            </p>
                            <p className="text-text-secondary">
                              Das Event selbst wird zusätzlich auf {" "}
                              <span className="font-mono">
                                {preview.resolution.canonicalVisitorId}
                              </span>{" "}
                              zugeordnet.
                            </p>
                          </div>
                        ) : (
                          <p className="text-success">
                            Würde an existierenden Visitor{" "}
                            <span className="font-mono">
                              {preview.resolution.canonicalVisitorId}
                            </span>{" "}
                            angehängt — kein Merge.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  </div>
                  )}
                </div>
              )}

              {(!isMergeReview || showTech) && (
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
                    Payload
                  </p>
                  <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? "Kopiert" : "JSON kopieren"}
                  </button>
                </div>
                <pre className="max-h-[40vh] overflow-auto rounded-md border border-border-primary bg-bg-tertiary p-3 font-mono text-xs leading-relaxed text-text-primary">
                  {json}
                </pre>
              </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2 border-t border-border-primary bg-bg-secondary px-5 py-3">
            {isMergeReview ? (
              <>
                {droppedArray.length > 0 ? (
                  // Power-user path: keys were dropped in the technical details,
                  // so offer the precise "ingest without these keys" action.
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => item && onReject(item.id)}
                      className="flex-1 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-danger-bg hover:text-danger"
                    >
                      🚫 Verschiedene Personen
                    </button>
                    <button
                      type="button"
                      onClick={() => item && onApprove(item.id, droppedArray)}
                      className="flex-1 rounded-md bg-accent px-3 py-2 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
                    >
                      Übernehmen ohne {droppedArray.length} Key
                      {droppedArray.length === 1 ? "" : "s"}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => item && onReject(item.id)}
                      className="flex flex-1 flex-col items-center rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm font-semibold text-text-primary transition-colors hover:bg-danger-bg hover:text-danger"
                    >
                      🚫 Verschiedene Personen
                      <span className="text-[11px] font-normal text-text-tertiary">
                        getrennt lassen
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => item && onApprove(item.id)}
                      className="flex flex-1 flex-col items-center rounded-md bg-accent px-3 py-2 text-sm font-semibold text-text-on-accent transition-colors hover:bg-accent-hover"
                    >
                      ✅ Dieselbe Person
                      <span className="text-[11px] font-normal opacity-80">
                        zusammenführen
                      </span>
                    </button>
                  </div>
                )}
                {droppedArray.length > 0 && (
                  <p className="text-center text-xs text-text-tertiary">
                    Ausgewählt zum Ausschluss:{" "}
                    <span className="font-mono">
                      {droppedArray.join(", ")}
                    </span>
                  </p>
                )}
              </>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => item && onReject(item.id)}
                  className="flex-1 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-danger-bg hover:text-danger"
                >
                  Ablehnen{" "}
                  <kbd className="ml-1 font-mono text-xs text-text-tertiary">
                    R
                  </kbd>
                </button>
                <button
                  type="button"
                  onClick={() => item && onApprove(item.id)}
                  className="flex-1 rounded-md bg-accent px-3 py-2 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
                >
                  Genehmigen{" "}
                  <kbd className="ml-1 font-mono text-xs opacity-60">A</kbd>
                </button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Kanal-Kürzel je Einstufung — deutsch, wie die Freigabe-Liste (TASK-57). */
function originChannelLabel(channel: ApprovalOriginChannel): string {
  switch (channel) {
    case "meta_ads":
      return "Meta Ads";
    case "google_ads":
      return "Google Ads";
    case "organic":
      return "Organic";
    case "unattributed":
      return "ohne Zuordnung";
    case "broken":
      return "Herkunft defekt";
    case "unknown":
      return "unbekannt";
  }
}

/**
 * Herkunfts-Block über dem Payload (TASK-58, A3). Zeigt Zeitpunkt des ersten
 * Kontakts, Quelle, Medium, Kampagnenname sowie dh_c/dh_as/dh_a. Bei
 * "broken" bleibt der Rohwert der Kampagne sichtbar statt des (dann leeren)
 * klassifizierten Namens — der Punkt ist ja gerade zu zeigen, was ankam.
 * Eine Hilfe, kein Sperrgrund: Approve/Reject unten hängen nie von dieser
 * Abfrage ab, egal wie sie ausgeht.
 */
function OriginBlock({
  approvalId,
  open,
}: {
  approvalId: number;
  open: boolean;
}) {
  const { data, isLoading, isError } = useApprovalOrigin(approvalId, open);

  return (
    <div className="mt-6 rounded-md border border-border-primary bg-bg-secondary px-4 py-3">
      <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Herkunft
      </p>
      {isLoading ? (
        <p className="text-xs text-text-tertiary">Lade Herkunft…</p>
      ) : isError || !data || data.channel === "unknown" ? (
        <p className="text-xs text-text-tertiary">
          Herkunft konnte nicht geladen werden — Genehmigen/Ablehnen bleibt möglich.
        </p>
      ) : data.channel === "unattributed" ? (
        <p className="text-xs text-text-tertiary">
          Keine Herkunft gefunden — ohne Zuordnung.
        </p>
      ) : (
        <div className="space-y-1.5 text-sm">
          <Field
            label="Kanal"
            value={originChannelLabel(data.channel)}
          />
          {data.channel === "broken" && (
            <p className="rounded-md border border-warning bg-warning-bg px-3 py-2 text-xs text-warning">
              Enthält unaufgelöste Platzhalter (z.&nbsp;B. {"{{campaign.name}}"}) — die
              Werte unten sind nicht verwertbar, aber im Rohzustand sichtbar.
            </p>
          )}
          <Field
            label="Erster Kontakt"
            value={
              data.occurredAt
                ? new Date(data.occurredAt).toLocaleString("de-DE")
                : "—"
            }
            mono
          />
          <Field label="Quelle" value={data.utmSource ?? "—"} mono />
          <Field label="Medium" value={data.utmMedium ?? "—"} mono />
          <Field
            label="Kampagne"
            value={
              (data.channel === "broken"
                ? data.utmCampaign
                : data.campaignName) ?? "—"
            }
            mono
          />
          <Field label="dh_c" value={data.dhC ?? "—"} mono />
          <Field label="dh_as" value={data.dhAs ?? "—"} mono />
          <Field label="dh_a" value={data.dhA ?? "—"} mono />
        </div>
      )}
    </div>
  );
}

/**
 * Plain-language summary of a merge-review for non-technical reviewers. Answers
 * one question: "are these the same person?" — showing the colliding strong
 * keys (e.g. two emails) as contact cards and the shared key that bridged them.
 */
function MergeReviewSummary({
  item,
  preview,
}: {
  item: ApprovalListItem;
  preview: PreviewResolution | undefined;
}) {
  const conflicts = item.mergeRisk?.strongKeyConflicts ?? [];
  const hasConflict = conflicts.length > 0;

  // The keys that bridged the visitors together (the "why they got merged").
  // Exclude any key type that's itself in conflict — that's shown as the cards.
  const conflictTypes = new Set(conflicts.map((c) => c.keyType));
  const bridges = (preview?.mergeBridges ?? []).filter(
    (b) => !conflictTypes.has(b.keyType),
  );
  const bridgeTypes = [...new Set(bridges.map((b) => b.keyType))];

  // Plausibility verdict from ALL bridges (incl. conflicting types — a
  // matching email is the strongest same-person signal there is).
  const verdict = assessMergePlausibility(preview?.mergeBridges ?? []);

  // Why is this row still with a human? The AI reviewer stamps
  // _merge_risk.ai_escalated when it looked and deliberately handed over.
  const riskCtx = item.payload["_merge_risk"] as
    | Record<string, unknown>
    | undefined;
  const aiNote =
    typeof riskCtx?.["ai_escalated"] === "string"
      ? (riskCtx["ai_escalated"] as string)
      : null;

  return (
    <div className="space-y-3 rounded-md border border-warning bg-warning-bg px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-warning">
        <Users size={16} />
        {hasConflict
          ? "Möglicherweise zwei Personen vermischt"
          : "Verdächtige Verknüpfung — bitte prüfen"}
      </div>

      {hasConflict ? (
        conflicts.map((c) => (
          <div key={c.keyType} className="space-y-1.5">
            <p className="text-xs text-text-secondary">
              Diese Person trägt {c.values.length} verschiedene{" "}
              <span className="font-medium text-text-primary">
                {conflictKeyLabel(c.keyType)}
              </span>{" "}
              — gehören die zur selben Person?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {c.values.map((v, i) => (
                <div
                  key={v}
                  className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-wider text-text-tertiary">
                    Kontakt {String.fromCharCode(65 + i)}
                  </p>
                  <p className="truncate text-sm text-text-primary" title={v}>
                    {v}
                  </p>
                  {v === item.email && item.firstName && (
                    <p className="text-xs text-text-secondary">
                      {item.firstName} {item.lastName ?? ""}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <p className="text-xs text-text-secondary">
          Dieses Event würde sich mit bestehenden Daten verknüpfen, die wie
          mehrere verschiedene Personen aussehen (z.B. eine Nummer, die schon
          bei vielen Kontakten auftaucht).
        </p>
      )}

      {bridgeTypes.length > 0 && (
        <p className="text-xs text-text-secondary">
          <span className="text-text-tertiary">Verbunden über:</span>{" "}
          {bridgeTypes.map((t) => bridgeLabel(t)).join(", ")}
        </p>
      )}

      {verdict && (
        <div
          className={`rounded-md border px-3 py-2 text-xs ${verdict.className}`}
        >
          <p className="font-semibold">{verdict.title}</p>
          <p className="mt-0.5 opacity-80">{verdict.detail}</p>
        </div>
      )}

      <p className="text-[11px] text-text-tertiary">
        <span className="font-medium">KI-Vorprüfung:</span>{" "}
        {aiNote ??
          "noch nicht erfolgt — der Fall wartet auf den nächsten KI-Durchlauf oder die KI-Prüfung ist deaktiviert."}
      </p>

      <div className="rounded-md bg-bg-primary/60 px-3 py-2 text-xs text-text-secondary">
        <p>
          <span className="font-medium text-success">Dieselbe Person</span> →
          werden zu einem Profil zusammengeführt.
        </p>
        <p>
          <span className="font-medium text-danger">Verschiedene Personen</span>{" "}
          → bleiben getrennt, dieses Event wird verworfen.
        </p>
      </div>

      <p className="text-[11px] text-text-tertiary">
        Betrifft {item.affectedEventCount} Aufruf
        {item.affectedEventCount === 1 ? "" : "e"}
        {item.lastSeenAt
          ? ` · zuletzt ${formatRelativeShort(item.lastSeenAt)}`
          : ""}
      </p>
    </div>
  );
}

/**
 * Deterministic same-person plausibility from the live merge bridges: number
 * of independent key types and their strength. Mirrors the worker's conflict
 * ladder so the operator sees the same logic that decided to gate the event.
 */
function assessMergePlausibility(
  bridges: Array<{ keyType: string; strength: KeyStrength }>,
): { title: string; detail: string; className: string } | null {
  if (bridges.length === 0) return null;
  const types = [...new Set(bridges.map((b) => b.keyType))];
  const hasStrong = bridges.some((b) => b.strength === "strong");
  const hasNonWeak = bridges.some((b) => b.strength !== "weak");
  const list = types.map((t) => bridgeLabel(t)).join(" + ");

  if (types.length >= 2 && hasStrong) {
    return {
      title: "Sehr wahrscheinlich dieselbe Person",
      detail: `${types.length} unabhängige Brücken (${list}) — mehrere Identifier bestätigen sich gegenseitig.`,
      className: "border-success bg-success-bg text-success",
    };
  }
  if (hasStrong || (hasNonWeak && types.length >= 2)) {
    return {
      title: "Wahrscheinlich dieselbe Person",
      detail: `Verbunden über ${list}.`,
      className: "border-success bg-success-bg text-success",
    };
  }
  if (hasNonWeak) {
    return {
      title: "Vermutlich dieselbe Person — ein Gerät, kein starker Identifier",
      detail: `Verbunden über ${list}. Geteilte Geräte (Familie, Büro) können täuschen.`,
      className: "border-warning bg-warning-bg text-warning",
    };
  }
  return {
    title: "Unsicher — nur über Click-IDs verbunden",
    detail: `Verbunden über ${list}. Geteilte Links erzeugen genau dieses Muster — im Zweifel getrennt lassen.`,
    className: "border-danger bg-danger-bg text-danger",
  };
}

function conflictKeyLabel(keyType: string): string {
  switch (keyType) {
    case "email":
      return "E-Mail-Adressen";
    case "phone":
      return "Telefonnummern";
    default:
      return keyType;
  }
}

function bridgeLabel(keyType: string): string {
  switch (keyType) {
    case "phone":
      return "dieselbe Telefonnummer";
    case "email":
      return "dieselbe E-Mail-Adresse";
    case "client_id":
      return "dasselbe Gerät (Cookie)";
    case "fbp":
    case "fbc":
      return "denselben Browser (Facebook-Cookie)";
    case "order_id":
      return "dieselbe Bestellnummer";
    default:
      return `denselben Wert (${keyType})`;
  }
}

function formatRelativeShort(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return `vor ${d} Tag${d === 1 ? "" : "en"}`;
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3">
      <div className="text-text-secondary">{label}</div>
      <div
        className={`text-text-primary ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function translateReason(r: string): string {
  switch (r) {
    case "multi_visitor_merge":
      return "Würde mehrere Visitors auf einmal verschmelzen";
    case "high_cardinality_key":
      return "Mindestens ein Key existiert bereits bei ≥3 verschiedenen Visitors (Fake-Verdacht / recycelter Key)";
    case "mega_visitor_join":
      return "Würde sich an einen Visitor anhängen, der schon ≥10 verschiedene client_ids gesammelt hat (Mega-Visitor / wahrscheinlich kontaminierter Identity-Bridge)";
    case "strong_key_conflict":
      return "Würde zwei verschiedene Identitäten (z.B. zwei E-Mail-Adressen oder zwei Telefonnummern) zu einer Person zusammenführen";
    default:
      return r;
  }
}

const STRENGTH_LABEL: Record<KeyStrength, string> = {
  strong: "stark — Identitäts-Key (z.B. Email, Telefon)",
  medium: "mittel — langlebiges Cookie / Browser-ID",
  weak: "schwach — Click-ID aus URL, häufig geteilt",
};

const STRENGTH_DOT_CLASS: Record<KeyStrength, string> = {
  strong: "bg-success",
  medium: "bg-warning",
  weak: "bg-danger",
};

function StrengthDot({ strength }: { strength: KeyStrength }) {
  return (
    <span
      className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${STRENGTH_DOT_CLASS[strength]}`}
      title={STRENGTH_LABEL[strength]}
      aria-label={`Key-Strength: ${strength}`}
    />
  );
}

function strengthBorderClass(strength: KeyStrength): string {
  switch (strength) {
    case "strong":
      return "border-success/40 bg-success/5";
    case "medium":
      return "border-warning/40 bg-warning-bg/30";
    case "weak":
      return "border-danger/40 bg-danger-bg/30";
  }
}

const ACTIVITY_LABEL: Record<ActivitySignal, string> = {
  new: "neu",
  recent: "aktiv",
  stale: "ruhig",
  dormant: "lange weg",
};

const ACTIVITY_CLASS: Record<ActivitySignal, string> = {
  new: "bg-bg-tertiary text-text-tertiary",
  recent: "bg-success/15 text-success",
  stale: "bg-warning-bg text-warning",
  dormant: "bg-danger-bg text-danger",
};

const ACTIVITY_TOOLTIP: Record<ActivitySignal, string> = {
  new: "Visitor hat noch keine Events — kein History-Signal",
  recent: "Visitor war in den letzten 7 Tagen aktiv — Merge wahrscheinlich legitim",
  stale: "Visitor war 7–90 Tage inaktiv — Merge neutral",
  dormant:
    "Visitor war länger als 90 Tage inaktiv — Match-Key könnte recycelt sein. Merge prüfen!",
};

function ActivitySignalBadge({ signal }: { signal: ActivitySignal }) {
  return (
    <span
      className={`ml-auto inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${ACTIVITY_CLASS[signal]}`}
      title={ACTIVITY_TOOLTIP[signal]}
    >
      {ACTIVITY_LABEL[signal]}
    </span>
  );
}

function formatDaysAgo(days: number): string {
  if (days === 0) return "heute zuletzt";
  if (days === 1) return "gestern zuletzt";
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 30) return `vor ${Math.floor(days / 7)} Wochen`;
  if (days < 365) return `vor ${Math.floor(days / 30)} Monaten`;
  return `vor ${Math.floor(days / 365)} Jahr${days >= 730 ? "en" : ""}`;
}
